import type { LoanApartmentWithShareholderFields } from './types'
import { PaymentProductBillingBasis } from '../enum'
import type { Apartment } from 'types/apartment'
import type { Loan, LoanShareholder } from '@viilu-web/shared/types/loans'
import {
	LoanEventType,
	LoanFinancingType,
	LoanInterestDaysMonth,
	LoanInterestDaysYear,
	LoanStatus,
} from '@viilu-web/shared'
import type { IntlShape } from 'react-intl'
import { loanEventTypeMessage, loanStatusMessage } from './messages'
import type { LoanCreateFormInputs } from './LoanCreateDialog/validation'
import { billingBasisMessage } from '../utils/messages'
import { formatCurrency } from 'util/formatStrings'
import { exhaustiveMatchingGuard } from '@viilu-web/shared/util/exhaustiveMatchingGuard'

export const isValidBillingBasis = (
	value: any,
): value is PaymentProductBillingBasis => {
	return Object.values(PaymentProductBillingBasis).includes(value)
}

export const mapShareholdersToSelectableSharegroups = (
	apartments: Apartment[],
	shareholders?: LoanShareholder[],
): LoanApartmentWithShareholderFields[] => {
	// If we are creating a loan for the first time and have not yet saved
	if (!shareholders) {
		const selectableSharegroups: LoanApartmentWithShareholderFields[] =
			apartments.map((apt) => {
				const billingBasisSquares = {
					...((apt.squaresAmount || 0) === 0
						? {
								locked: false,
								amount: 0,
						  }
						: {
								locked: true,
								amount: apt.squaresAmount || 0,
						  }),
				}
				const billingBasisWeightedSquares = {
					...((apt.weightedSquaresAmount || 0) === 0
						? {
								locked: false,
								amount: 0,
						  }
						: {
								locked: true,
								amount: apt.weightedSquaresAmount || 0,
						  }),
				}
				const billingBasisShares = {
					...((apt.shareCount || 0) === 0
						? {
								locked: false,
								amount: 0,
						  }
						: {
								locked: true,
								amount: apt.shareCount || 0,
						  }),
				}
				const billingBasisResidents = {
					...((apt.userUUIDs?.length || 0) === 0
						? {
								locked: false,
								amount: 0,
						  }
						: {
								locked: true,
								amount: apt.userUUIDs.length,
						  }),
				}
				const billingBasisPieces = {
					locked: false,
					amount: 0,
				}

				return {
					...apt,
					units: 0,
					selected: false,
					billingBasisSquares,
					billingBasisWeightedSquares,
					billingBasisShares,
					billingBasisResidents,
					billingBasisPieces,
				}
			})
		return selectableSharegroups
	} else {
		// We have a created loan with shareholders we need to map to apartments
		const selectableSharegroups: LoanApartmentWithShareholderFields[] =
			apartments.map((apt) => {
				const correspondingShareholder = shareholders.find(
					(sh) => apt.uuid === sh.shareGroupUUID,
				)
				const billingBasisSquares = {
					...((apt?.squaresAmount || 0) === 0
						? {
								locked: false,
								amount: 0,
						  }
						: {
								locked: true,
								amount: apt.squaresAmount || 0,
						  }),
				}
				const billingBasisWeightedSquares = {
					...((apt.weightedSquaresAmount || 0) === 0
						? {
								locked: false,
								amount: 0,
						  }
						: {
								locked: true,
								amount: apt.weightedSquaresAmount || 0,
						  }),
				}
				const billingBasisShares = {
					...((apt.shareCount || 0) === 0
						? {
								locked: false,
								amount: 0,
						  }
						: {
								locked: true,
								amount: apt.shareCount || 0,
						  }),
				}
				const billingBasisResidents = {
					...((apt.userUUIDs?.length || 0) === 0
						? {
								locked: false,
								amount: 0,
						  }
						: {
								locked: true,
								amount: apt.userUUIDs.length,
						  }),
				}
				const billingBasisPieces = {
					locked: false,
					amount: 0,
				}

				if (correspondingShareholder) {
					return {
						...apt,
						selected: true,
						companyName: correspondingShareholder.companyName,
						companyUUID: correspondingShareholder.companyUUID,
						originalLoanShareAmount:
							correspondingShareholder.originalLoanShareAmount,
						billingBasis: correspondingShareholder.billingBasis,
						contractUUID: correspondingShareholder.contractUUID ?? undefined,
						billingBasisSquares:
							correspondingShareholder.billingBasisSquares ??
							billingBasisSquares,
						billingBasisWeightedSquares:
							correspondingShareholder.billingBasisWeightedSquares ??
							billingBasisWeightedSquares,
						billingBasisShares:
							correspondingShareholder.billingBasisShares ?? billingBasisShares,
						billingBasisResidents:
							correspondingShareholder.billingBasisResidents ??
							billingBasisResidents,
						billingBasisPieces:
							correspondingShareholder.billingBasisPieces ?? billingBasisPieces,
					}
				} else {
					return {
						...apt,
						units: 0,
						selected: false,
						billingBasisSquares,
						billingBasisWeightedSquares,
						billingBasisShares,
						billingBasisResidents,
						billingBasisPieces,
					}
				}
			})
		return selectableSharegroups
	}
}

export const calculateTotalSelectedUnits = (
	sharegroups: LoanApartmentWithShareholderFields[],
	billingBasis: PaymentProductBillingBasis,
): number => {
	return Object.values(sharegroups).reduce((acc, sg) => {
		if (!sg.selected) return acc

		switch (billingBasis) {
			case PaymentProductBillingBasis.AREA:
				return acc + (sg?.billingBasisSquares?.amount || 0)
			case PaymentProductBillingBasis.AREA_ADJUSTED:
				return acc + (sg?.billingBasisWeightedSquares?.amount || 0)
			case PaymentProductBillingBasis.SHARE_COUNT:
				return acc + (sg?.billingBasisShares?.amount || 0)
			case PaymentProductBillingBasis.RESIDENT_COUNT:
				return acc + (sg?.billingBasisResidents?.amount || 0)
			case PaymentProductBillingBasis.PIECES:
				return acc + (sg?.billingBasisPieces?.amount || 0)
			default:
				return exhaustiveMatchingGuard(billingBasis, acc)
		}
	}, 0)
}

export const mapLoanToFormInputs = (loan: Loan) => {
	// Need to set default values so form states don't change from uncontrolled to controlled
	const formInputs: LoanCreateFormInputs = {
		uuid: loan.uuid || '',
		name: loan.name || '',
		companyUUID: loan.companyUUID || '',
		companyName: loan.companyName || '',
		financingType: loan.financingType ?? LoanFinancingType.CAPITAL,
		startDateEstimated: loan.startDateEstimated || '',
		endDateEstimated: loan.endDateEstimated || '',
		originalLoanAmount: loan.originalLoanAmount ?? 0,
		sharesPayable: loan.sharesPayable ?? false,
		sharePaymentDates: loan.sharePaymentDates || [],
		usableOnBuildingManagerCertificate:
			loan.usableOnBuildingManagerCertificate ?? false,
		interestDaysMonth:
			loan.interestDaysMonth || LoanInterestDaysMonth.THIRTY_DAYS,
		interestDaysYear: loan.interestDaysYear || LoanInterestDaysYear.THREE_SIXTY,
		referenceNumber: loan.referenceNumber || '',
		status: loan.status ?? LoanStatus.DRAFT,
		currency: loan.currency || 'EUR',
		startDate: loan.startDate || '',
		endDate: loan.endDate || '',
		type: loan.type || '',
		grantedBy: loan.grantedBy || '',
		duration: loan.duration || '',
		amountDrawn: loan.amountDrawn ?? 0,
		dateDrawn: loan.dateDrawn || '',
		drawn: loan.drawn ?? false,
		interestType: loan.interestType || '',
		startingInterestPercentage: loan.startingInterestPercentage ?? 0,
		interestFirstDueDate: loan.interestFirstDueDate || '',
		interestInspectionDates: loan.interestInspectionDates || [],
		marginalPercentage: loan.marginalPercentage ?? 0,
		amortizationAmount: loan.amortizationAmount ?? 0,
		amortizationFirstDueDate: loan.amortizationFirstDueDate || '',
		amortizationDates: loan.amortizationDates || [],
		startingUnits: loan.startingUnits ?? 0,
		billingBasis: loan.billingBasis || PaymentProductBillingBasis.AREA,
		currentFormStep: loan.currentFormStep ?? 0,
		reachedFormStep: loan.reachedFormStep ?? 0,
		shareholders: loan.shareholders || [],
		selectableSharegroups: [],
	}
	return formInputs
}

/**
 * Recursive function that converts empty string values in an object (even nested object) into null
 */
export const convertEmptyStringsToNull = <T extends Record<string, any>>(
	obj: T,
): T => {
	if (obj === null || obj === undefined) return obj
	return Object.fromEntries(
		Object.entries(obj).map(([key, value]) => {
			if (value === '') return [key, null]
			if (Array.isArray(value))
				return [key, value.map(convertEmptyStringsToNull)]
			if (typeof value === 'object' && value !== null)
				return [key, convertEmptyStringsToNull(value)]
			return [key, value]
		}),
	) as T
}

export const loanEventTypeOptions = (intl: IntlShape) => {
	return Object.values(LoanEventType).map((eventType) => ({
		label: loanEventTypeMessage(intl, eventType),
		value: eventType,
	}))
}

export const getLoanRowValue = (
	loan: Loan,
	key: keyof Loan | 'balance',
	intl: IntlShape,
	type: 'string' | 'currency' | 'billingBasis' | 'loanStatus',
) => {
	const value = loan[key]

	switch (type) {
		case 'string': {
			return value as string
		}
		case 'billingBasis': {
			return billingBasisMessage(intl, value as PaymentProductBillingBasis)
		}
		case 'currency': {
			return formatCurrency(value as number)
		}
		case 'loanStatus': {
			return loanStatusMessage(intl, value as LoanStatus)
		}
		default: {
			return exhaustiveMatchingGuard(type)
		}
	}
}
